import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listAdminCatches } from '../../api/admin';
import { EditCatchDialog } from '../catches/EditCatchDialog';
import type { CatchResponse } from '../../types/api';
import { inputClass } from '../ui/inputClass';
import { CatchTable } from '../catches/CatchTable';
import { fromCatchResponse } from '../catches/catchRow';

export function AdminCatchesTab() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<{ userId: string; clubId: string; from: string; to: string }>({
    userId: '', clubId: '', from: '', to: '',
  });
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<CatchResponse | null>(null);

  const queryKey = ['admin-catches', filters, page];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => listAdminCatches({
      userId: filters.userId || undefined,
      clubId: filters.clubId ? Number(filters.clubId) : undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      page,
      pageSize: 50,
    }),
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['admin-catches'] });
  }

  return (
    <div>
      <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-4">
        <input
          placeholder="User ID"
          value={filters.userId}
          onChange={(e) => { setPage(1); setFilters(f => ({ ...f, userId: e.target.value })); }}
          className={inputClass}
        />
        <input
          placeholder="Club ID"
          type="number"
          value={filters.clubId}
          onChange={(e) => { setPage(1); setFilters(f => ({ ...f, clubId: e.target.value })); }}
          className={inputClass}
        />
        <input
          type="date"
          value={filters.from}
          onChange={(e) => { setPage(1); setFilters(f => ({ ...f, from: e.target.value })); }}
          className={inputClass}
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => { setPage(1); setFilters(f => ({ ...f, to: e.target.value })); }}
          className={inputClass}
        />
      </div>

      {isLoading && <div className="p-4">Loading catches…</div>}
      {error && <div className="p-4 text-red-500">Failed to load catches.</div>}

      {data && (
        <>
          <CatchTable
            rows={data.items.map(fromCatchResponse)}
            columns={{ spot: true, angler: true, session: true }}
            renderActions={(row) => {
              const original = data.items.find(c => c.id === row.id);
              if (!original) return null;
              return (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEditing(original); }}
                  className="text-xs text-teal-600 hover:underline"
                >
                  Edit
                </button>
              );
            }}
          />

          {data.items.length === 0 && (
            <div className="p-4 text-slate-500">No catches match.</div>
          )}

          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              Page {data.page} • {data.totalCount} total
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="rounded border px-3 py-1 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={page * data.pageSize >= data.totalCount}
                onClick={() => setPage(p => p + 1)}
                className="rounded border px-3 py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      <EditCatchDialog
        catchItem={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refetch(); }}
        onDeleted={() => { setEditing(null); refetch(); }}
      />
    </div>
  );
}
